import { createContext, useState, useEffect, useContext, useCallback, useMemo, useRef } from 'react';
import { brandApi } from '../api/brands';
import { useAuth } from './AuthContext';
import logger from '../utils/logger';

const BrandContext = createContext();

// ── Constants ─────────────────────────────────────────────
const STORAGE_KEY = 'preferredBrand';
const BRAND_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
const DEFAULT_BRAND_SLUG = 'gabhan-command-center';

export const BrandProvider = ({ children }) => {
  const { user } = useAuth();
  const [currentBrand, setCurrentBrand] = useState(null);
  const [availableBrands, setAvailableBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetchedAt, setLastFetchedAt] = useState(null);

  const mountedRef = useRef(true);
  const _fetchTimeoutRef = useRef(null);

  useEffect(() => {
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (user) {
      loadBrands();
    } else {
      setCurrentBrand(null);
      setAvailableBrands([]);
      setLoading(false);
      setError(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // ── Multi-tab sync ──────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (e.key === STORAGE_KEY && e.newValue && mountedRef.current) {
        const synced = availableBrands.find(b => b.slug === e.newValue);
        if (synced) setCurrentBrand(synced);
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [availableBrands]);

  const loadBrands = useCallback(async (force = false) => {
    // Skip if cache is fresh (unless forced)
    if (!force && lastFetchedAt && Date.now() - lastFetchedAt < BRAND_CACHE_TTL_MS) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await brandApi.getActiveBrands();
      const brands = response.brands || [];

      if (!mountedRef.current) return;

      setAvailableBrands(brands);
      setLastFetchedAt(Date.now());

      // Determine current brand
      const preferredSlug = localStorage.getItem(STORAGE_KEY);
      let selected = null;

      if (preferredSlug) {
        selected = brands.find(b => b.slug === preferredSlug);
      }

      if (!selected && brands.length > 0) {
        selected = brands.find(b => b.slug === DEFAULT_BRAND_SLUG) || brands[0];
      }

      setCurrentBrand(selected);

      if (selected) {
        localStorage.setItem(STORAGE_KEY, selected.slug);
      }
    } catch (err) {
      logger.apiError('/brands/active', err);
      if (mountedRef.current) {
        setError(err.response?.status === 401 ? 'session_expired' : 'fetch_failed');
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [lastFetchedAt]);

  const switchBrand = useCallback((brand) => {
    if (!brand) return;
    setCurrentBrand(brand);
    localStorage.setItem(STORAGE_KEY, brand.slug);

    // Dispatch event for other modules that may need to react
    try {
      window.dispatchEvent(new CustomEvent('brand-switched', { detail: { brand } }));
    } catch (_e) { /* safe */ }

    logger.info(`[Brand] Switched to: ${brand.name || brand.slug}`);
  }, []);

  // ── Brand lookup helpers ────────────────────────────────
  const getBrandBySlug = useCallback((slug) => {
    return availableBrands.find(b => b.slug === slug) || null;
  }, [availableBrands]);

  const getBrandById = useCallback((id) => {
    return availableBrands.find(b => b._id === id || b.id === id) || null;
  }, [availableBrands]);

  // ── Derived state ───────────────────────────────────────
  const brandCount = availableBrands.length;
  const hasBrands = brandCount > 0;
  const isMultiBrand = brandCount > 1;

  const brandTheme = useMemo(() => {
    if (!currentBrand) return null;
    return {
      name: currentBrand.name,
      slug: currentBrand.slug,
      logo: currentBrand.logo || currentBrand.logoUrl || null,
      primaryColor: currentBrand.primaryColor || currentBrand.color || '#0066CC',
      secondaryColor: currentBrand.secondaryColor || null,
      favicon: currentBrand.favicon || null,
    };
  }, [currentBrand]);

  const value = useMemo(() => ({
    currentBrand,
    setCurrentBrand: switchBrand,
    availableBrands,
    loading,
    error,
    refreshBrands: () => loadBrands(true),

    // Helpers
    getBrandBySlug,
    getBrandById,
    brandTheme,

    // Derived
    brandCount,
    hasBrands,
    isMultiBrand,
    lastFetchedAt,
  }), [
    currentBrand, switchBrand, availableBrands, loading, error,
    loadBrands, getBrandBySlug, getBrandById, brandTheme,
    brandCount, hasBrands, isMultiBrand, lastFetchedAt,
  ]);

  return (
    <BrandContext.Provider value={value}>
      {children}
    </BrandContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useBrand = () => {
  const context = useContext(BrandContext);
  if (!context) {
    throw new Error('useBrand must be used within BrandProvider');
  }
  return context;
};
